<!--
  The megaphone in the app navbar. A dot shows when an entry went public after
  this member last opened What's new.

  Hover and focus show the panel and mark nothing. A click, a tap or Enter on
  the megaphone, or on an entry in the panel, records the watermark the panel
  was built with (POST /whats-new/seen) and then follows the link. A mouse
  passing over the icon never clears the dot.
-->
<template>
  <VDropdown
    v-if="whatsNew"
    placement="bottom-end"
    :aria-id="ariaId"
    popper-class="whats-new-popover"
    class="inline-flex"
    :disabled="isTooSmallScreenToDisplayDropdown"
    :triggers="['hover', 'focus']"
    :popper-triggers="['hover', 'focus']"
    :delay="{ show: 120, hide: 220 }"
    :distance="10"
    @apply-show="isOpen = true"
    @apply-hide="isOpen = false"
  >
    <NavbarIconItem
      screen-reader-text="What's new"
      href="/whats-new"
      class="whats-new-trigger"
      :class="{ 'is-open': isOpen }"
      title="What's new"
      test_id="whats-new-icon"
      @click="markSeen"
    >
      <MegaphoneIcon class="size-6 xl:size-4.5" aria-hidden="true" />
      <span v-if="hasUnseen" class="whats-new-dot" aria-hidden="true" />
    </NavbarIconItem>

    <template #popper="{ hide }">
      <div class="whats-new-panel">
        <p class="whats-new-heading">What’s new</p>
        <div v-if="whatsNew.entries.length" class="whats-new-list">
          <InertiaLink
            v-for="entry in whatsNew.entries"
            :key="entry.slug"
            :href="`/whats-new/${entry.slug}`"
            class="whats-new-row"
            @click="markSeen(), hide()"
          >
            <ReleaseThumbnail :entry="entry" />
            <div class="whats-new-copy">
              <p class="whats-new-title">{{ entry.title }}</p>
              <time :datetime="entry.shippedOn" class="whats-new-date">{{ entry.shippedLabel }}</time>
            </div>
          </InertiaLink>
        </div>
        <p v-else class="px-5 py-4 text-sm text-neutral-400">Nothing new yet.</p>
        <InertiaLink href="/whats-new" class="whats-new-footer" @click="markSeen(), hide()">
          View all updates <ArrowRightIcon class="size-3.5" aria-hidden="true" />
        </InertiaLink>
      </div>
    </template>
  </VDropdown>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import NavbarIconItem from '@/shared/components/navbar/NavbarIconItem.vue'
import ReleaseThumbnail from '@/modules/site/components/whats-new/ReleaseThumbnail.vue'
import MegaphoneIcon from '~icons/lucide/megaphone'
import ArrowRightIcon from '~icons/lucide/arrow-right'
import { useScreenWidth } from '@/shared/composables/useScreenWidth'

type PanelEntry = {
  slug: string
  title: string
  shippedOn: string
  shippedLabel: string
}

type WhatsNewProp = {
  hasUnseen: boolean
  watermark: string | null
  entries: PanelEntry[]
}

const { screenWidth, tailwindScreens } = useScreenWidth()
const ariaId = useId()
const page = usePage()
const isOpen = ref(false)
const seenThisVisit = ref(false)

const whatsNew = computed(
  () => (page.props.shared as { whatsNew?: WhatsNewProp | null } | undefined)?.whatsNew ?? null,
)
const hasUnseen = computed(() => !!whatsNew.value?.hasUnseen && !seenThisVisit.value)

// Below xs the panel has nowhere to go, so the icon is a plain link.
const isTooSmallScreenToDisplayDropdown = computed(
  () => screenWidth.value < (tailwindScreens.value.xs ?? 0),
)

// The dot clears at once; the server marker moves forward to the watermark
// the panel was built with, never to "now", so an entry published after this
// page loaded stays unseen. Failure is left alone: the dot comes back on the
// next page load and the next click tries again.
function markSeen(): void {
  if (!whatsNew.value?.watermark || seenThisVisit.value) return
  seenThisVisit.value = true
  axios.post('/whats-new/seen', { watermark: whatsNew.value.watermark }).catch(() => {
    seenThisVisit.value = false
  })
}
</script>

<style scoped>
.whats-new-trigger.is-open { background: #f3f0f9; }
.whats-new-dot {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 9px;
  height: 9px;
  border: 2px solid white;
  border-radius: 50%;
  background: var(--color-secondary-500);
}
.whats-new-trigger.is-open .whats-new-dot { border-color: #f3f0f9; }
.whats-new-panel {
  width: 352px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border-radius: 14px;
  background: white;
}
.whats-new-heading {
  padding: 19px 20px 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-neutral-800);
}
.whats-new-list { padding: 0 8px 8px; }
.whats-new-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  color: var(--color-neutral-800);
  transition: background-color 150ms;
}
.whats-new-row:hover,
.whats-new-row:focus-visible {
  background: #f7f7fa;
  color: var(--color-neutral-800);
}
.whats-new-row:focus-visible,
.whats-new-footer:focus-visible {
  outline: 2px solid var(--color-secondary-300);
  outline-offset: -2px;
}
.whats-new-copy { min-width: 0; }
.whats-new-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 19px;
}
.whats-new-date {
  display: block;
  margin-top: 5px;
  font-size: 11px;
  line-height: 15px;
  color: var(--color-neutral-400);
}
.whats-new-footer {
  display: flex;
  align-items: center;
  gap: 7px;
  border-top: 1px solid var(--color-neutral-100);
  padding: 15px 20px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: var(--color-secondary-600);
  transition: background-color 150ms;
}
.whats-new-footer:hover {
  background: #faf9fc;
  color: var(--color-secondary-700);
}
</style>

<style>
/* Only this popover overrides Floating Vue's shared dropdown chrome. */
.whats-new-popover.v-popper--theme-dropdown .v-popper__inner {
  border: 1px solid var(--color-neutral-100) !important;
  border-radius: 14px !important;
  box-shadow: 0 8px 28px -8px #1b25451f, 0 2px 6px #1b254506 !important;
}
</style>
